import { signal } from "@preact/signals";
import type { EventEnvelope, User, TablesConfig } from "@orderman/shared";
import { settings } from "./config.js";
import { localOnline, cloudRead } from "./sync.js";

/**
 * Roher Event-Feed der Veranstaltung für die Admin-Auswertung (Auswertung/Bestellungen/Bons).
 * Die Kellner-Bestell-Views brauchen ihn NICHT — die haben ihre projizierten Orders bereits.
 * Hier ziehen wir das ganze Log (wie die Portal-Konsole), um clientseitig `dashboardStats`,
 * `projectOrders` und die `print.requested`-Bons zu berechnen.
 *
 * Quelle: vor Ort der lokale Server (`GET /api/events`, LAN-offen), im Mobilfunk das Cloud-Relay
 * (`GET /events.php` mit read-only-Token). Kein Account-Login — wie der übrige App-Lesepfad.
 */
export const adminEvents = signal<EventEnvelope[] | null>(null);
export const adminLoading = signal(false);
export const adminError = signal<string | null>(null);

/**
 * Ungespeicherte Änderungen im aktuell offenen Editor (Personal/Tische, die ihren Formular-
 * State beim Sektionswechsel verlieren). Die Navigation fragt nach, bevor sie verwirft.
 */
export const adminDirty = signal(false);

interface EventsPage {
  events: EventEnvelope[];
  nextCursor: string;
}

async function pull(url: string, headers: Record<string, string>): Promise<EventEnvelope[]> {
  const all: EventEnvelope[] = [];
  const limit = 5000;
  let since = "0";
  for (;;) {
    const res = await fetch(`${url}&since=${encodeURIComponent(since)}&limit=${limit}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as EventsPage;
    all.push(...(data.events ?? []));
    if (!data.events || data.events.length < limit) break;
    since = data.nextCursor;
  }
  return all;
}

/**
 * Aktuelle Personal-/Tische-Konfiguration (jeweils neuester Snapshot inkl. Version) aus dem
 * geladenen Feed ziehen — dieselbe Idee wie `extractConfig` im Portal. Basis für die Editoren:
 * anzeigen = Daten, speichern = Version+1.
 */
export function configFromFeed(events: EventEnvelope[] | null): {
  users: User[];
  usersVersion: number;
  tables: TablesConfig;
  tablesVersion: number;
} {
  let users: User[] = [];
  let usersVersion = 0;
  let tables: TablesConfig = { ranges: [], extras: [] };
  let tablesVersion = 0;
  for (const env of events ?? []) {
    const b = env.body;
    if (b.kind !== "config.snapshot") continue;
    if (b.configKind === "users" && b.version >= usersVersion) {
      usersVersion = b.version;
      users = (b.data as User[]) ?? [];
    } else if (b.configKind === "tables" && b.version >= tablesVersion) {
      tablesVersion = b.version;
      tables = (b.data as TablesConfig) ?? { ranges: [], extras: [] };
    }
  }
  return { users, usersVersion, tables, tablesVersion };
}

/** Event-Feed (neu) laden. Wählt automatisch lokalen Server (LAN) oder Cloud-Relay (Mobilfunk). */
export async function loadAdminEvents(): Promise<void> {
  adminLoading.value = true;
  adminError.value = null;
  try {
    const venue = encodeURIComponent(settings.value.venueId);
    if (localOnline.value) {
      adminEvents.value = await pull(`${settings.value.serverUrl}/api/events?venue=${venue}`, {});
      return;
    }
    const cloud = cloudRead();
    if (!cloud?.readKey) {
      throw new Error("Kein Server erreichbar (kein LAN, kein Cloud-Lesezugang).");
    }
    adminEvents.value = await pull(`${cloud.url}/events.php?venue=${venue}`, { "X-Api-Key": cloud.readKey });
  } catch (e) {
    adminError.value = e instanceof Error ? e.message : String(e);
    if (adminEvents.value === null) adminEvents.value = [];
  } finally {
    adminLoading.value = false;
  }
}
